import { useEffect, useState } from "react";
import type { RcOfferingsPayload } from "./types";

/**
 * Where the native wrapper parks the RevenueCat offerings it fetched, and the
 * event it fires when it pushes a fresh copy.  Both names are wire contract
 * with `mobile/shell/components/utils/injectPricingData.ts` — change one side
 * and prices silently stop appearing in the app.
 */
export const OFFERINGS_STORAGE_KEY = "revcat.offerings.v1";
export const OFFERINGS_EVENT = "revcat:offerings";

/** Offerings older than this are ignored: stale prices are worse than none. */
export const DEFAULT_OFFERINGS_TTL_MS = 24 * 60 * 60 * 1000;

/**
 * Read the RevenueCat offerings the native wrapper injected.
 *
 * Two arrival paths, because either can happen first: the wrapper writes to
 * `localStorage` on every fetch (so a page loaded later still has prices), and
 * it dispatches a `CustomEvent` when it pushes a fresh set (so a page already
 * open updates).  Returns `null` on web, where there is no native layer.
 */
export function useOfferings(
  ttlMs: number = DEFAULT_OFFERINGS_TTL_MS
): RcOfferingsPayload | null {
  const [offerings, setOfferings] = useState<RcOfferingsPayload | null>(null);

  useEffect(() => {
    if (typeof window === "undefined") return;

    const isFresh = (ts?: number) =>
      typeof ts === "number" && Date.now() - ts < ttlMs;
    const selectFresh = (incoming: any): RcOfferingsPayload | null =>
      incoming && isFresh(incoming.fetchedAt) ? incoming : null;

    try {
      const cached = window.localStorage.getItem(OFFERINGS_STORAGE_KEY);
      if (cached) {
        try {
          const fresh = selectFresh(JSON.parse(cached));
          if (fresh) setOfferings(fresh);
        } catch {
          /* malformed cache: fall through to the live event */
        }
      }
    } catch {
      /* localStorage can throw (private mode, blocked storage) */
    }

    const handler = (e: any) => {
      const fresh = e && e.detail ? selectFresh(e.detail) : null;
      if (fresh) setOfferings(fresh);
    };
    document.addEventListener(OFFERINGS_EVENT, handler as any);
    return () => document.removeEventListener(OFFERINGS_EVENT, handler as any);
  }, [ttlMs]);

  return offerings;
}
